import { WheelGameSettings } from '@kanek/shared-data-access';
import { WheelProps, AlignText } from 'spin-wheel';
import { loadOptionalWheelImage } from '../../utils/image-loader';

/**
 * Maps WheelGameSettings to spin-wheel's WheelProps.
 * Ensures Portal and Experience App use identical configuration.
 */
export class WheelConfigMapper {
  /**
   * Convert WheelGameSettings to WheelProps with image loading.
   * This is the single source of truth for wheel configuration.
   *
   * IMPORTANT: This method is ASYNC because it loads images.
   * The spin-wheel library requires actual HTMLImageElement objects, not URLs.
   *
   * @param settings - Wheel game settings from database
   * @param items - Array of prize items
   * @returns Promise that resolves with complete WheelProps (including loaded images)
   */
  static async toWheelPropsAsync(
    settings: WheelGameSettings,
    items: Array<{ label?: string; [key: string]: unknown }>
  ): Promise<WheelProps> {
    // Load images if URLs are provided
    const [backgroundImage, overlayImage] = await Promise.all([
      loadOptionalWheelImage(settings.image),
      loadOptionalWheelImage(settings.overlayImage)
    ]);

    return {
      // Visual settings
      borderColor: settings.borderColor,
      borderWidth: settings.borderWidth,
      radius: settings.radius,

      // Label settings
      itemLabelRadius: settings.itemLabelRadius,
      itemLabelRadiusMax: settings.itemLabelRadiusMax,
      itemLabelRotation: settings.itemLabelRotation,
      itemLabelAlign: settings.itemLabelAlign as AlignText,
      itemLabelColors: settings.itemLabelColors,
      itemLabelBaselineOffset: -0.07, // Fixed offset for consistent label positioning
      itemLabelFontSizeMax: settings.itemLabelFontSizeMax,
      itemLabelFont: settings.itemLabelFont || 'Codec Pro',
      itemLabelStrokeColor: settings.itemLabelStrokeColor,
      itemLabelStrokeWidth: settings.itemLabelStrokeWidth,

      // Behavior settings
      isInteractive: settings.isInteractive,
      rotationSpeedMax: settings.rotationSpeedMax,
      rotationResistance: settings.rotationResistance,

      // Line settings
      lineWidth: settings.lineWidth,
      lineColor: settings.lineColor,

      // Background settings
      itemBackgroundColors: settings.itemBackgroundColors,

      // Images (loaded HTMLImageElement objects, not URLs!)
      image: backgroundImage,
      overlayImage: overlayImage,

      // Items (prizes)
      items,
    };
  }

  /**
   * DEPRECATED: Use toWheelPropsAsync() instead.
   * This synchronous version doesn't support images.
   * Kept for backwards compatibility only.
   */
  static toWheelProps(settings: WheelGameSettings, items: Array<{ label?: string; [key: string]: unknown }>): WheelProps {
    console.warn('⚠️ WheelConfigMapper.toWheelProps() is deprecated. Use toWheelPropsAsync() for image support.');

    return {
      // Visual settings
      borderColor: settings.borderColor,
      borderWidth: settings.borderWidth,
      radius: settings.radius,

      // Label settings
      itemLabelRadius: settings.itemLabelRadius,
      itemLabelRadiusMax: settings.itemLabelRadiusMax,
      itemLabelRotation: settings.itemLabelRotation,
      itemLabelAlign: settings.itemLabelAlign as AlignText,
      itemLabelColors: settings.itemLabelColors,
      itemLabelBaselineOffset: -0.07, // Fixed offset for consistent label positioning
      itemLabelFontSizeMax: settings.itemLabelFontSizeMax,
      itemLabelFont: settings.itemLabelFont || 'Codec Pro',
      itemLabelStrokeColor: settings.itemLabelStrokeColor,
      itemLabelStrokeWidth: settings.itemLabelStrokeWidth,

      // Behavior settings
      isInteractive: settings.isInteractive,
      rotationSpeedMax: settings.rotationSpeedMax,
      rotationResistance: settings.rotationResistance,

      // Line settings
      lineWidth: settings.lineWidth,
      lineColor: settings.lineColor,

      // Background settings
      itemBackgroundColors: settings.itemBackgroundColors,

      // Items (prizes)
      items,
    };
  }

  /**
   * Get default WheelGameSettings.
   * Matches wheel-configurator.component.ts form defaults (Left preset).
   * These defaults are used when creating new short codes and resetting to defaults.
   */
  static getDefaultSettings(): WheelGameSettings {
    return {
      type: 'wheel',
      // Container settings
      wheelSize: 'medium',        // Default to 768px wheel (medium preset)
      
      // Visual settings
      borderColor: '#FF6B35',     // Warm orange (matches form defaults)
      borderWidth: 2,
      radius: 1.0,                // Full size wheel (100% of container)

      // Label settings (Left preset - matches form hardcoded values)
      itemLabelRadius: 0.85,      // Label position (85% from center)
      itemLabelRadiusMax: 0.2,    // Label max width (20% of radius)
      itemLabelRotation: 180,     // Rotated for left alignment
      itemLabelAlign: 'left',     // Left-aligned labels (default preset)
      itemLabelColors: ['#FFFFFF'], // White labels for better contrast
      itemLabelFont: 'Codec Pro',
      itemLabelFontSizeMax: 100,
      itemLabelStrokeColor: '#000000',
      itemLabelStrokeWidth: 0,

      // Behavior settings
      isInteractive: false,
      rotationSpeedMax: 250,
      rotationResistance: -35,
      enableSoundEffects: true,   // Enable sound effects by default

      // Line settings
      lineWidth: 1,
      lineColor: '#FF6B35',       // Matches border color

      // Background settings - warm palette
      itemBackgroundColors: [
        '#FF6B35',  // Warm orange (primary)
        '#2C3E50',  // Dark slate blue
        '#E74C3C',  // Red
        '#FF6B35',  // Warm orange
        '#FF6B35',  // Warm orange
        '#7AC943',  // Light green
      ],

      // 3D Cylinder settings (matches form fallback defaults)
      cylinderHeight: 0.25,
      cylinderColor: '#ed1f24',   // Kanek red
      cylinderOpacity: 0.8,
      cylinderRoughness: 0.5,
      cylinderMetalness: 0.0,

      // 3D Arrow settings (matches form fallback defaults)
      arrowColor: '#222',
      arrowWidth: 0.64,
      arrowHeight: 0.32,
      arrowDepth: 0.05,
      castShadow: true,

      // Optional fields
      image: '',
      overlayImage: '',
    };
  }
}
